import { win } from '@wix/cashier-common/dist/src/utils/win';
import { waitForCondition } from '@wix/cashier-common/dist/src/utils/waitForCondition';
import { type Expiration } from '../../types/Expiration';
import { TokenizationErrorCodes } from '../../types/TokenizationErrorCodes';

// https://github.com/2Checkout/node-examples

interface TokenizeParams {
  sellerId: string;
  publishableKey: string;
  card: {
    number: string;
    cvv: string;
    expire: Expiration;
  };
}

interface TokenizeRequest {
  sellerId: string;
  publishableKey: string;
  ccNo: string;
  cvv: string;
  expMonth: number;
  expYear: number;
}

export const tokenizeVia2Checkout = (params: TokenizeParams): Promise<string> =>
  new Promise<string>(async (resolve, reject) => {
    const _reject = (message: string, errorCode = TokenizationErrorCodes.GeneralTokenizationError) =>
      reject({ errorCode, message });

    try {
      if (!win.TCO) {
        try {
          await waitForCondition(() => !!win.TCO, {
            errorMessage: `Wait for TwoCheckout SDK to load timeout`,
            timeout: 5000,
          });
        } catch {
          console.error('2Checkout TCO script is not loaded!');
          return _reject('Missing 2Checkout TCO object to perform tokenization');
        }
      }

      const { sellerId, publishableKey, card } = params;

      if (!sellerId || !publishableKey || !card.number || !card.cvv || !card.expire.month || !card.expire.year) {
        console.error('Did you provide all required fields? ccNo, cvv, expMonth, sellerId, publishableKey');
        return _reject('Missed fields', TokenizationErrorCodes.GeneralValidationError);
      }

      const tokenizeRequest: TokenizeRequest = {
        sellerId: params.sellerId,
        publishableKey: params.publishableKey,
        ccNo: params.card.number,
        cvv: params.card.cvv,
        expMonth: params.card.expire.month,
        expYear: params.card.expire.year,
      };

      // TODO: add max time awaiting?
      await new Promise<void>((onLoad) => {
        win.TCO.loadPubKey('production', () => {
          onLoad();
        });
      });

      win.TCO.requestToken((data) => resolve(data.response.token.token), _reject, tokenizeRequest);
    } catch (error) {
      console.error(`tokenize via 2Checkout failed`, error);
      _reject(`tokenize via 2Checkout failed ${error}`);
    }
  });
